import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiSettings,
  FiUser,
  FiShield,
  FiLock,
  FiBell,
  FiSliders,
  FiKey,
  FiUsers,
  FiMenu,
  FiX,
} from "react-icons/fi";
import ProfileSettings from "./sections/ProfileSettings";
import PrivacySettings from "./sections/PrivacySettings";
import SecuritySettings from "./sections/SecuritySettings";
import NotificationSettings from "./sections/NotificationSettings";
import ContentSettings from "./sections/ContentSettings";
import EncryptionSettings from "./sections/EncryptionSettings";
import AccountSettings from "./sections/AccountSettings";
import "./SettingsPage.css";

// Navigation sections configuration
const settingsSections = [
  {
    id: "general",
    title: "General",
    items: [
      {
        id: "profile",
        label: "Profile",
        icon: <FiUser />,
        component: ProfileSettings,
        description: "Manage your profile information",
      },
      {
        id: "account",
        label: "Account",
        icon: <FiUsers />,
        component: AccountSettings,
        description: "Account settings and actions",
      },
    ],
  },
  {
    id: "privacy-security",
    title: "Privacy & Security",
    items: [
      {
        id: "privacy",
        label: "Privacy",
        icon: <FiShield />,
        component: PrivacySettings,
        description: "Control your privacy settings",
      },
      {
        id: "security",
        label: "Security",
        icon: <FiLock />,
        component: SecuritySettings,
        description: "Two-factor authentication & more",
      },
      {
        id: "encryption",
        label: "Encryption",
        icon: <FiKey />,
        component: EncryptionSettings,
        description: "Manage encryption keys",
      },
    ],
  },
  {
    id: "preferences",
    title: "Preferences",
    items: [
      {
        id: "notifications",
        label: "Notifications",
        icon: <FiBell />,
        component: NotificationSettings,
        description: "Notification preferences",
      },
      {
        id: "content",
        label: "Content",
        icon: <FiSliders />,
        component: ContentSettings,
        description: "Muted users & hidden content",
      },
    ],
  },
];

// Animation variants
const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("profile");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Get section from URL hash or default to profile
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      const allItems = settingsSections.flatMap((s) => s.items);
      const exists = allItems.find((item) => item.id === hash);
      if (exists) {
        setActiveSection(hash);
      }
    }
  }, [location.hash]);

  // Update URL when section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    navigate(`/settings#${sectionId}`, { replace: true });
    setIsMobileMenuOpen(false);
  };

  // Get current section component
  const getCurrentSection = () => {
    const allItems = settingsSections.flatMap((s) => s.items);
    const current = allItems.find((item) => item.id === activeSection);
    return current?.component || ProfileSettings;
  };

  const ActiveComponent = getCurrentSection();

  return (
    <motion.div
      className="stg-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Mobile Menu Toggle */}
      <button
        className="stg-mobile-toggle"
        onClick={() => setIsMobileMenuOpen(true)}
        aria-label="Open menu"
      >
        <FiMenu />
      </button>

      {/* Mobile Overlay */}
      <div
        className={`stg-sidebar-overlay ${
          isMobileMenuOpen ? "stg-visible" : ""
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`stg-sidebar ${isMobileMenuOpen ? "stg-open" : ""}`}>
        {/* Sidebar Header */}
        <div className="stg-sidebar-header">
          <div className="stg-sidebar-header-left">
            <button
              className="stg-sidebar-back"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
          </div>
          {/* Close button for mobile */}
          <button
            className="stg-sidebar-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <FiX />
          </button>
        </div>

        {/* Title */}
        <div style={{ padding: "0 24px 16px" }}>
          <h1 className="stg-sidebar-title">
            <FiSettings />
            Settings
          </h1>
        </div>

        {/* Navigation */}
        <nav className="stg-sidebar-nav">
          {settingsSections.map((section) => (
            <div key={section.id} className="stg-nav-section">
              <div className="stg-nav-section-title">{section.title}</div>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  className={`stg-nav-item ${
                    activeSection === item.id ? "stg-active" : ""
                  }`}
                  onClick={() => handleSectionChange(item.id)}
                >
                  <span className="stg-nav-item-icon">{item.icon}</span>
                  <span className="stg-nav-item-text">{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="stg-main">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <ActiveComponent />
          </motion.div>
        </AnimatePresence>
      </main>
    </motion.div>
  );
};

export default SettingsPage;
